import { storeToRefs } from 'pinia';
import { nextTick, Ref } from 'vue';
import OrderAPI from '@/apps/order/classes/api/OrderAPI';
import {
  useOrdersCreateCalculatorStore,
  useOrdersCreateDataPreparedStore,
  useOrdersCreateFormDataStore,
} from '@/apps/order/stores/ordersCreate';
import { useFeatureStore } from '@/apps/order/stores';
import DataPreparedParamsInterface from '@/apps/order/interfaces/ordersCreate/DataPreparedParamsInterface';

import { DataPreparedCustomFieldsInterface } from '@/apps/order/interfaces/ordersCreate/DataPreparedCustomFieldsInterface';

let alreadyFetched = false;
let fetchedWithSalesPartnerNumber: string | undefined;

const hasDataPreparedStepCustomFields = async (): Promise<void> => new Promise<void>(
  (resolve, reject) => {
    nextTick(async () => {
      try {
        const dataPreparedStore = useOrdersCreateDataPreparedStore();
        const { customFields } = storeToRefs(dataPreparedStore) as {
          customFields: Ref<DataPreparedCustomFieldsInterface | undefined>
        };

        const dataCalculatorPreparedStore = useOrdersCreateCalculatorStore();
        const { formData } = storeToRefs(dataCalculatorPreparedStore);

        const formDataStore = useOrdersCreateFormDataStore();
        const { selectedTariffs, formData: orderCreateFormData } = storeToRefs(formDataStore);

        if (customFields.value !== undefined && alreadyFetched) {
          resolve();
          return;
        }

        const currentSalesPartnerNumber = formData.value.salesPartnerNumber
          ? `${formData.value.salesPartnerNumber}`
          : undefined;
        const categories = orderCreateFormData.value?.categories ?? null;

        const shouldFetch = !alreadyFetched
          || fetchedWithSalesPartnerNumber !== currentSalesPartnerNumber
          || customFields.value === undefined;

        if (shouldFetch) {
          // Check if customFields feature is active before making API calls
          const featureStore = useFeatureStore();
          
          // Ensure features are loaded
          if (featureStore.features.length === 0) {
            try {
              await featureStore.fetch();
            } catch (error) {
              console.error('Failed to fetch features:', error);
              // If we can't fetch features, assume customFields is not active to prevent loop
              customFields.value = {
                fields: [],
                sections: [],
              };
              fetchedWithSalesPartnerNumber = currentSalesPartnerNumber;
              alreadyFetched = true;
              resolve();
              return;
            }
          }

          const isCustomFieldsFeatureActive = featureStore.isEnabled('customFields');

          if (!isCustomFieldsFeatureActive) {
            // Feature is not active, set empty custom fields and resolve
            customFields.value = {
              fields: [],
              sections: [],
            };
            fetchedWithSalesPartnerNumber = currentSalesPartnerNumber;
            alreadyFetched = true;
            resolve();
            return;
          }

          const params: DataPreparedParamsInterface = {};
          if (currentSalesPartnerNumber) {
            params.salesPartnerNumber = currentSalesPartnerNumber;
          }
          params.brand_id = formData.value.brand_id;

          if (categories !== null && categories.length > 0) {
            if (Object.keys(selectedTariffs.value).length === 0) {
              resolve();
              return;
            }

            // prepare parallel requests for each category
            const requests = categories
              .filter((el: string) => selectedTariffs.value[el] && selectedTariffs.value[el].id)
              .map((el: string) => {
                const p = { ...params };
                p.product_id = selectedTariffs.value[el].id;
                return OrderAPI.fetchDataPreparedStepCustomFields(p).catch((error) => {
                  console.error(`Failed to fetch custom fields for category ${el}:`, error);
                  return undefined;
                });
              });

            const results = await Promise.all(requests);

            if (results.some((r) => r === undefined)) {
              reject(new Error('Failed to fetch custom fields data'));
              return;
            }

            // merge results by unique field.id
            const fetchedCustomFields = new Map();
            const fetchedCustomSections = new Map();
            results.forEach((res) => {
              if (res && res.fields) {
                res.fields.forEach((field) => {
                  if (!fetchedCustomFields.has(field.id)) {
                    fetchedCustomFields.set(field.id, field);
                  }
                });
              }

              if (res && res.sections) {
                res.sections.forEach((section) => {
                  if (!fetchedCustomSections.has(section.id)) {
                    fetchedCustomSections.set(section.id, section);
                  }
                });
              }
            });

            customFields.value = {
              fields: Array.from(fetchedCustomFields.values()),
              sections: Array.from(fetchedCustomSections.values()),
            };
            fetchedWithSalesPartnerNumber = currentSalesPartnerNumber;
          } else {
            await OrderAPI.fetchDataPreparedStepCustomFields(params).then((data) => {
              customFields.value = data;
              fetchedWithSalesPartnerNumber = currentSalesPartnerNumber;
            }).catch((error) => {
              console.error('Failed to fetch custom fields:', error);
              reject(new Error('Failed to fetch custom fields data'));
              return;
            });
          }

          if (formData.value.brand_id) {
            alreadyFetched = true;
          }
        }

        if (customFields.value === undefined) {
          reject(new Error('missing data prepared'));
          return;
        }

        const customFieldsValue = customFields.value as DataPreparedCustomFieldsInterface;

        if (customFieldsValue?.fields && customFieldsValue.fields.length > 0) {
          formData.value.customFields = customFieldsValue.fields.reduce(
            (acc: Record<string, string>, field: { key: string }) => {
              acc[field.key] = '';
              return acc;
            },
            {},
          );
        }

        resolve();
      } catch (e) {
        reject(new Error(['fetch data prepared failed', e].join(' ')));
      }
    });
  },
);

export default hasDataPreparedStepCustomFields;
